import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { FormikErrors, useFormik } from 'formik';
import { shouldNavigateToFinance } from 'utils/navigation';
import { clearTimer, isTimerAlreadyStarted } from 'utils/timeoutUtility';
import { scrollToElement, ValidationError } from 'utils';
import { useClientQuery, usePhoneVerification } from 'hooks/webapi';
import useSmartlook from 'hooks/useSmartlook';
import { getIsProdEnv } from 'utils/getEnv';
import useClientPhoneQuery from 'hooks/webapi/client/useClientPhoneQuery';
import { minLength } from 'utils/validations';
import { isNil, omitBy } from 'lodash';
import * as R from 'Router/routes';
import { useTranslation } from 'react-i18next';
import {
  NEW_CODE_TIMER,
  PHONE_CALL_TIMER,
  SECONDS_TO_WAIT_RESEND,
} from '../../../constants';

type FormValues = {
  verificationCode: string;
};

const validate = ({ verificationCode }: FormValues) => {
  const errors: FormikErrors<FormValues> = {};
  errors.verificationCode = minLength(4)(verificationCode);
  return omitBy(errors, isNil);
};

const useVerification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [codeAlreadySent] = useState(() =>
    isTimerAlreadyStarted(SECONDS_TO_WAIT_RESEND, NEW_CODE_TIMER),
  );
  // A code is sent automatically on init
  const [newCodeTimerVisible, setNewCodeTimerVisible] = useState(true);
  const [phoneCallTimerVisible, setPhoneCallTimerVisible] = useState(() => {
    if (codeAlreadySent)
      return isTimerAlreadyStarted(SECONDS_TO_WAIT_RESEND, PHONE_CALL_TIMER);

    // Fresh init: drop an expired start time so the counter runs the full duration
    clearTimer(PHONE_CALL_TIMER);
    return true;
  });

  const codeRequested = useRef(codeAlreadySent);

  const [pending, setPending] = useState(false);
  // Sync guard against double submit (state updates are async)
  const submitting = useRef(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const { sendCode, requestPhoneVerification, requestPhoneCallVerification } =
    usePhoneVerification();
  const {
    data: client,
    isLoading,
    refetch: fetchClient,
  } = useClientQuery(true);
  const { data: clientPhone, isLoading: clientPhoneIsLoading } =
    useClientPhoneQuery(true);

  const { identify } = useSmartlook();

  useEffect(() => {
    if (getIsProdEnv()) identify(client?.number);
    return () => {
      clearTimer(NEW_CODE_TIMER);
      clearTimer(PHONE_CALL_TIMER);
    };
  }, []);

  useEffect(() => {
    const prefetch = async () => {
      try {
        // const recaptchaToken = await handleRecaptchaVerify();
        if (!isLoading && clientPhone?.mobilePhone && !codeRequested.current) {
          codeRequested.current = true;
          await getNewCode();
        }
      } catch {
        /* empty */
      }
    };
    prefetch();
  }, [clientPhone?.mobilePhone, isLoading]);

  const getNewCode = useCallback(async () => {
    if (!clientPhone?.mobilePhone) {
      return;
    }

    setNewCodeTimerVisible(true);

    try {
      await requestPhoneVerification(clientPhone?.mobilePhone);
    } catch {
      /* empty */
    }
  }, [clientPhone?.mobilePhone, requestPhoneVerification]);

  const getPhoneCall = useCallback(async () => {
    if (!clientPhone?.mobilePhone) {
      return;
    }

    setPhoneCallTimerVisible(true);

    try {
      await requestPhoneCallVerification();
    } catch {
      /* empty */
    }
  }, [clientPhone?.mobilePhone, requestPhoneCallVerification]);

  const handleSubmit = useCallback(
    async ({ verificationCode }: FormValues) => {
      if (submitting.current) return;
      submitting.current = true;

      try {
        setPending(true);
        await sendCode(verificationCode);
        const { data: fetchedClient } = await fetchClient();

        const nextRoute = shouldNavigateToFinance(fetchedClient)
          ? R.FINANCE
          : R.BANK_ID;

        navigate(nextRoute);
      } catch (e) {
        if (e instanceof ValidationError && e?.fieldErrors?.length) {
          if (e.fieldErrors[0].property === 'verificationCode') {
            setServerError(t('error:validation.incorrect_code'));
            scrollToElement(e.fieldErrors[0].property);
          }
        }

        submitting.current = false;
        setPending(false);
      }
    },
    [sendCode, navigate, t],
  );

  const formik = useFormik<{ verificationCode: string }>({
    initialValues: {
      verificationCode: '',
    },
    validate,
    onSubmit: handleSubmit,
  });

  // Each countdown drops its own stored start time, so the next click starts fresh
  const handleOnNewCodeTimeout = useCallback(() => {
    clearTimer(NEW_CODE_TIMER);
    setNewCodeTimerVisible(false);
  }, []);

  const handleOnPhoneCallTimeout = useCallback(() => {
    clearTimer(PHONE_CALL_TIMER);
    setPhoneCallTimerVisible(false);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      formik.setFieldValue('verificationCode', value);
      if (serverError) setServerError(null);
    },
    [serverError],
  );

  const isDisabled = Boolean(formik.values.verificationCode.length < 4);

  return {
    clientPhoneIsLoading,
    formik,
    handleChange,
    getNewCode,
    getPhoneCall,
    handleOnNewCodeTimeout,
    handleOnPhoneCallTimeout,
    newCodeTimerVisible,
    phoneCallTimerVisible,
    clientPhone,
    pending,
    serverError,
    isDisabled,
    SECONDS_TO_WAIT_RESEND,
  };
};

export default useVerification;
